import { Gift, Handshake } from 'lucide-react';

interface Props {
  referrerName: string | null;
  referrerType: 'partner' | 'organization' | null;
  trialDays: number;
}

/**
 * Banner compatto Notion-style mostrato sotto al NotionStepIndicator quando
 * l'utente sta completando la registrazione tramite un codice referral
 * (partner o organizzazione). Evidenzia il vantaggio (trial esteso) per non
 * perdere il contesto durante i 4 step del flusso.
 *
 * Renderizza null se non c'e' un referrer attivo, cosi puo' essere inserito
 * incondizionatamente in ogni step.
 *
 * Palette allineata al tema notion-register-* delle pagine register:
 *   #37352F   testo primario
 *   #787774   testo secondario
 *   #2383E2   accent (giorni evidenziati)
 *   #F7F7F5   sfondo neutro chiaro
 *   rgba(55,53,47,0.09)  border discreto
 */
export function RegistrationReferralBanner({ referrerName, referrerType, trialDays }: Props) {
  if (!referrerName || !referrerType) return null;

  const isPartner = referrerType === 'partner';
  const Icon = isPartner ? Handshake : Gift;
  const verb = isPartner ? 'consigliato da' : 'invitato da';

  return (
    <div
      className="mb-4 flex items-center gap-2.5 px-3 py-2 rounded-md bg-[#F7F7F5] border border-[rgba(55,53,47,0.09)]"
      role="note"
      aria-label="Stai usando un codice referral"
    >
      <div className="flex items-center justify-center w-7 h-7 rounded-md bg-white border border-[rgba(55,53,47,0.06)] flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#787774]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0 leading-tight">
        <p className="text-[11.5px] text-[#787774] m-0">
          {verb}{' '}
          <span className="font-semibold text-[#37352F]">{referrerName}</span>
        </p>
        <p className="text-[10.5px] text-[#787774] m-0 mt-0.5">
          Hai sbloccato{' '}
          <span className="font-semibold text-[#2383E2]">{trialDays} giorni</span>{' '}
          di prova
          {isPartner ? '' : ' + 30% sul primo mese'}
        </p>
      </div>
    </div>
  );
}
